import { format, parseISO } from 'date-fns'
import fr from 'date-fns/locale/fr'

/**
 * Réunions CSE : libellés, formats et utilitaires partagés par la liste
 * (List.vue), le dépôt (Create.vue) et la fiche (One.vue).
 */

export const MEETING_ROLES = ['admin', 'community-manager', 'director', 'operator']

/** Rafraîchissement d'une réunion en cours de traitement. */
export const POLL_INTERVAL_MS = 5000

export const PROCESSING_STATUSES = ['queued', 'transcribing', 'synthesizing']

export const STATUS_LABELS = {
  queued: 'En attente',
  transcribing: 'Transcription',
  reviewing: 'À relire',
  synthesizing: 'Rédaction',
  completed: 'Terminée',
  failed: 'Échec'
}

export const INPUT_TYPE_LABELS = {
  audio: 'Enregistrement audio',
  text: 'Notes écrites'
}

export const INPUT_TYPE_ICONS = {
  audio: 'fas fa-microphone',
  text: 'fas fa-align-left'
}

export const FOLLOW_UP_STATUS_LABELS = {
  decided: 'Décidé',
  pending: 'En attente',
  to_investigate: 'À creuser'
}

/** Extensions acceptées au dépôt, alignées sur StoreMeetingRequest::AUDIO_EXTENSIONS. */
export const AUDIO_EXTENSIONS = ['mp3', 'm4a', 'wav', 'webm', 'mp4', 'mpga', 'mpeg', 'ogg', 'oga', 'opus', 'flac', 'aac', 'aiff', 'wma', 'amr', '3gp']

/** 1 Go, comme `services.ai.meetings.max_upload_kilobytes` : dix heures d'enregistrement à 200 kbit/s. */
export const MAX_UPLOAD_BYTES = 1024 * 1024 * 1024

export function isProcessing (status) {
  return PROCESSING_STATUSES.includes(status)
}

export function statusLabel (status) {
  return STATUS_LABELS[status] || status || '—'
}

export function inputTypeLabel (type) {
  return INPUT_TYPE_LABELS[type] || type || '—'
}

export function followUpStatusLabel (status) {
  return FOLLOW_UP_STATUS_LABELS[status] || status || '—'
}

/** Texte en minuscules sans accents, pour la recherche de la liste. */
export function normalizeText (value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
}

export function fileExtension (name) {
  const match = String(name || '').toLowerCase().match(/\.([a-z0-9]+)$/)
  return match ? match[1] : ''
}

function toDate (value) {
  if (!value) {
    return null
  }
  const date = value instanceof Date ? value : parseISO(String(value))
  return isNaN(date.getTime()) ? null : date
}

/**
 * Date de réunion. Le cast `date` de l'API sérialise minuit (Europe/Paris) en
 * UTC (« 2026-10-26T23:00:00Z » pour le 27) : on convertit l'instant en heure
 * locale avant de ne garder que le jour, sans tronquer la chaîne.
 */
export function formatMeetingDate (value) {
  const date = toDate(value)
  return date ? format(date, 'd MMMM yyyy', { locale: fr }) : ''
}

export function formatDateTime (value) {
  const date = toDate(value)
  return date ? format(date, 'dd/MM/yyyy à HH:mm', { locale: fr }) : ''
}

export function formatLogTime (value) {
  const date = toDate(value)
  return date ? format(date, 'dd/MM HH:mm:ss') : ''
}

export function formatFileSize (bytes) {
  const size = Number(bytes)
  if (!size || size <= 0) {
    return ''
  }
  if (size < 1024) {
    return `${size} o`
  }
  if (size < 1024 * 1024) {
    return `${Math.round(size / 1024)} Ko`
  }
  if (size < 1024 * 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(1).replace('.', ',')} Mo`
  }
  return `${(size / (1024 * 1024 * 1024)).toFixed(2).replace('.', ',')} Go`
}

export function formatDuration (seconds) {
  const total = Math.round(Number(seconds))
  if (!total || total <= 0) {
    return ''
  }
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const rest = total % 60
  if (hours) {
    return `${hours} h ${String(minutes).padStart(2, '0')} min`
  }
  if (minutes) {
    return `${minutes} min ${String(rest).padStart(2, '0')} s`
  }
  return `${rest} s`
}

export function formatTimecode (seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const rest = total % 60
  const minutesSeconds = `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`
  return hours ? `${hours}:${minutesSeconds}` : minutesSeconds
}

export function formatNumber (value) {
  const number = Number(value)
  return Number.isFinite(number) ? number.toLocaleString('fr-FR') : ''
}

export function escapeHtml (value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * Regroupe les segments du mot à mot en prises de parole : une entrée par
 * suite de segments d'un même locuteur, locuteurs renumérotés dans l'ordre
 * d'apparition (« Locuteur 1 », « Locuteur 2 »…) ou, quand le relecteur l'a
 * nommé (`names`, objet « étiquette brute → nom »), désignés par ce nom, avec
 * les positions du premier et du dernier segment de la prise de parole. Deux
 * étiquettes qui portent le même nom sont une seule personne : leurs prises de
 * parole consécutives sont réunies et partagent la couleur de la première.
 * Vide quand le fournisseur n'a identifié personne : la fiche affiche alors
 * le texte brut.
 */
export function speakerParagraphs (segments, names = {}) {
  if (!Array.isArray(segments) || !segments.length) {
    return []
  }
  if (!segments.some(segment => segment && hasSpeaker(segment))) {
    return []
  }

  const speakerIndexes = new Map()
  const colourIndexes = new Map()
  const paragraphs = []
  segments.forEach((segment, position) => {
    if (!segment) {
      return
    }
    const text = String(segment.text || '').trim()
    if (!text) {
      return
    }
    const raw = hasSpeaker(segment) ? String(segment.speaker) : '?'
    if (!speakerIndexes.has(raw)) {
      speakerIndexes.set(raw, speakerIndexes.size + 1)
    }
    const index = speakerIndexes.get(raw)
    const name = names && names[raw] ? String(names[raw]).trim() : ''
    const speaker = name || `Locuteur ${index}`
    if (!colourIndexes.has(speaker)) {
      colourIndexes.set(speaker, index)
    }
    const last = paragraphs[paragraphs.length - 1]
    if (last && last.speaker === speaker) {
      last.text += ` ${text}`
      last.end = segment.end
      last.lastIndex = position
      return
    }
    paragraphs.push({
      index: colourIndexes.get(speaker),
      raw,
      label: `Locuteur ${index}`,
      speaker,
      start: segment.start,
      end: segment.end,
      firstIndex: position,
      lastIndex: position,
      text
    })
  })
  return paragraphs
}

function hasSpeaker (segment) {
  return segment.speaker !== null && segment.speaker !== undefined && segment.speaker !== ''
}

/**
 * Les voix du mot à mot, les plus présentes en premier : étiquette brute du
 * fournisseur, numéro d'apparition (celui de « Locuteur n »), temps de parole,
 * nombre de prises de parole et la plus longue d'entre elles, à écouter pour
 * reconnaître la voix.
 */
export function speakerSummary (segments) {
  const voices = new Map()
  speakerParagraphs(segments).forEach(paragraph => {
    if (!voices.has(paragraph.raw)) {
      voices.set(paragraph.raw, { raw: paragraph.raw, index: paragraph.index, label: paragraph.label, seconds: 0, turns: 0, sample: null })
    }
    const voice = voices.get(paragraph.raw)
    const length = Number(paragraph.end) - Number(paragraph.start)
    voice.turns += 1
    if (Number.isFinite(length) && length > 0) {
      voice.seconds += length
      if (!voice.sample || length > voice.sample.length) {
        voice.sample = { start: Number(paragraph.start), end: Number(paragraph.end), length }
      }
    }
  })
  return Array.from(voices.values()).sort((a, b) => (b.seconds - a.seconds) || (a.index - b.index))
}

export function copyToClipboard (text) {
  const value = String(text || '')
  if (navigator.clipboard && window.isSecureContext) {
    // L'API moderne peut être refusée (permission, iframe) : on retombe
    // alors sur la copie par sélection.
    return navigator.clipboard.writeText(value).catch(() => legacyCopy(value))
  }
  return legacyCopy(value)
}

function legacyCopy (value) {
  return new Promise((resolve, reject) => {
    const textarea = document.createElement('textarea')
    textarea.value = value
    textarea.setAttribute('readonly', '')
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    const copied = document.execCommand('copy')
    document.body.removeChild(textarea)
    if (copied) {
      resolve()
    } else {
      reject(new Error('Copie impossible'))
    }
  })
}

/**
 * Erreurs d'une réponse 422 : messages par champ connu, le reste en vrac.
 * Sans `errors`, le `message` de l'API sert de message général.
 */
export function validationErrors (err, knownFields = []) {
  const data = err && err.response && err.response.data ? err.response.data : {}
  const errors = data.errors && typeof data.errors === 'object' ? data.errors : {}
  const fields = {}
  const general = []
  Object.keys(errors).forEach(key => {
    const messages = [].concat(errors[key]).filter(Boolean).map(String)
    if (knownFields.includes(key)) {
      fields[key] = messages
    } else {
      general.push(...messages)
    }
  })
  if (!Object.keys(fields).length && !general.length && data.message) {
    general.push(String(data.message))
  }
  return { fields, general }
}
